import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useQueries } from '@tanstack/react-query';

import { userAtom } from '../utils/store';
import { getRoles, getPrivileges, getPermissions } from '../screens/userManagement/request';

// Roles that bypass all permission checks
const SUPER_ADMIN_ROLES = ['admin', 'super admin'];

const isAdminRole = (roles: string[]) =>
  roles.some((r) => SUPER_ADMIN_ROLES.includes(r.toLowerCase()));

/**
 * Returns allowedPaths: Set<string> | null
 *   null  → full access (Admin role or data unavailable — fail-open)
 *   Set   → only the paths in the set are accessible
 */
export const usePermissions = () => {
  const user = useAtomValue(userAtom);
  const userRoles = user?.role ?? [];
  const isFullAccess = isAdminRole(userRoles);

  const [rolesQuery, privilegesQuery, permissionsQuery] = useQueries({
    queries: [
      {
        queryKey: ['get-all-roles'],
        queryFn: getRoles,
        enabled: !!user && !isFullAccess,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['get-all-privileges'],
        queryFn: getPrivileges,
        enabled: !!user && !isFullAccess,
        staleTime: 5 * 60 * 1000,
      },
      {
        queryKey: ['get-all-permissions'],
        queryFn: getPermissions,
        enabled: !!user && !isFullAccess,
        staleTime: 5 * 60 * 1000,
      },
    ],
  });

  const isLoading =
    !isFullAccess &&
    (rolesQuery.isLoading || privilegesQuery.isLoading || permissionsQuery.isLoading);

  const allowedPaths = useMemo<Set<string> | null>(() => {
    if (!user) return new Set();
    // Admin role → unrestricted
    if (isFullAccess) return null;
    // Any query errored → fail-open so the user isn't completely locked out
    if (rolesQuery.isError || privilegesQuery.isError || permissionsQuery.isError) return null;
    // Still loading
    if (!rolesQuery.data || !privilegesQuery.data || !permissionsQuery.data) return new Set();

    const roles = rolesQuery.data;
    const privileges = privilegesQuery.data.data ?? [];
    const permissions = permissionsQuery.data.data ?? [];

    // If no privileges have been configured at all, fail-open
    if (privileges.length === 0) return null;

    // Map role name → id for the user's assigned roles
    const userRoleIds = roles
      .filter((r) => userRoles.map((u) => u.toLowerCase()).includes(r.name.toLowerCase()))
      .map((r) => r.id);

    // Collect all permissionIds from matching privileges
    const allowedPermissionIds = new Set<number>();
    privileges
      .filter((p) => userRoleIds.includes(p.roleId))
      .forEach((p) => p.permissionId.forEach((id) => allowedPermissionIds.add(id)));

    // Collect pathNames from those permissions
    const paths = new Set<string>();
    permissions
      .filter((p) => allowedPermissionIds.has(p.id) && p.pathName)
      .forEach((p) => paths.add(p.pathName));

    return paths;
  }, [
    user,
    isFullAccess,
    rolesQuery.data,
    rolesQuery.isError,
    privilegesQuery.data,
    privilegesQuery.isError,
    permissionsQuery.data,
    permissionsQuery.isError,
    userRoles,
  ]);

  /**
   * Check whether a given pathname is accessible.
   * Handles dynamic segments: /applicants is allowed → /applicants/:id is also allowed.
   */
  const canAccess = (path: string): boolean => {
    if (allowedPaths === null) return true;
    if (allowedPaths.has(path)) return true;
    // Parent-path inheritance: /applicants allows /applicants/some-id
    return Array.from(allowedPaths).some(
      (allowed) => path.startsWith(allowed + '/') || path === allowed,
    );
  };

  return { allowedPaths, canAccess, isLoading, isFullAccess };
};
